import React, { memo } from 'react'
import { Text, Dropdown, SelectOption } from '@evertel/web/ui'
import Wrapper from './Wrapper'

export interface DropdownWriteWidgetProps {
    id: string
    htmlId?: string
    value: string
    onChange: (id: string, value: any) => void
    title: string
    required?: boolean
    placeholder?: string
    hint?: string
    allowMultiple?: boolean
    errors?: string
    options: SelectOption[]
    scope?: string
}

const DropdownWriteWidget: React.FC<DropdownWriteWidgetProps> = memo(({
    id,
    htmlId,
    value = '',
    onChange,
    title,
    required,
    placeholder = 'Select...',
    options,
    allowMultiple = false,
    errors,
    scope,
    ...otherProps
}) => {

    const inputId = `input-${htmlId}`
    const labelId = `label-${htmlId}`
    const descriptionId = `description-${htmlId}`
    const errorId = `error-${htmlId}`
    const hasErrors = errors && errors.length > 0

    return (
        <Wrapper
            title={title}
            required={required}
            errors={errors}
            labelId={labelId}
            descriptionId={descriptionId}
            errorId={errorId}
            {...otherProps}
        >
            <Dropdown
                inputId={inputId}
                value={value}
                options={options}
                isMulti={allowMultiple}
                placeholder={placeholder}
                isDisabled={scope === 'schemaBuilder'}
                isClearable={!required}
                onChange={(selectedOption) => {
                    if (!selectedOption) {
                        onChange?.(id, '')
                        return
                    }
                    if (Array.isArray(selectedOption)) {
                        // Handle multi-select case
                        onChange?.(id, selectedOption.map(option => option.value))
                    } else {
                        // Handle single-select case
                        onChange?.(id, (selectedOption as any).value)
                    }
                }}
                aria-labelledby={labelId}
                aria-invalid={hasErrors ? true : undefined}
                aria-errormessage={hasErrors ? errorId : undefined}
                aria-required={required ? true : undefined}
            />
        </Wrapper>
    )
})

// ----------------------------------------------------------------------------------------

export interface DropdownReadWidgetProps {
    id?: string
    htmlId?: string
    value?: string
    title?: string
    empty?: string
    options?: [{value: string, label: string}]
}

const DropdownReadWidget: React.FC<DropdownReadWidgetProps> = ({
    htmlId,
    value = '',
    title,
    empty,
    options,
    ...otherProps
}) => {

    const inputId = `input-${htmlId}`
    const labelId = `label-${htmlId}`
    const descriptionId = `description-${htmlId}`

    // Handle both single value and array of values
    const displayValue = React.useMemo(() => {
        // Create default empty state once
        const defaultEmpty = (typeof empty !== 'undefined') ? empty : (
            <>
                <span className="visually-hidden">None Selected</span>
                <span aria-hidden="true">---</span>
            </>
        )

        // Early return if no value
        if (!value) {
            return defaultEmpty
        }

        // Handle arrays and single values consistently
        const values = Array.isArray(value) ? value : [value]
        const selectedLabels = values
            .map(v => options?.find(opt => opt.value === v)?.label)
            .filter(Boolean)

        // Return labels or default empty state
        return selectedLabels.length > 0
            ? (Array.isArray(value) ? selectedLabels.join(', ') : selectedLabels[0])
            : defaultEmpty
    }, [value, options, empty])

    return (
        <Wrapper
            value={value}
            title={title}
            empty={empty}
            labelId={labelId}
            descriptionId={descriptionId}
            {...otherProps}
        >
            <Text id={inputId}>
                {displayValue}
            </Text>
        </Wrapper>
    )
}

export {
    DropdownWriteWidget,
    DropdownReadWidget
}
